import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Globe,
  KeyRound,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  Trash2,
  Image as ImageIcon,
  UserCircle2,
  Upload,
  Moon,
} from 'lucide-react';
import { Button, Card, Input, Pill, useToast } from '@nako/ui';
import { isHealthcareBusiness } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useRegionStore } from '@/stores/region';
import { type RegionCode } from '@nako/core';
import { supabase } from '@/lib/supabase';

const TIER_PRICES: Record<string, Record<string, string>> = {
  BW: { free: 'P 0', smart: 'P 99 / month', pro: 'P 299 / month', enterprise: 'P 999 / month' },
  ZA: { free: 'R 0', smart: 'R 150 / month', pro: 'R 450 / month', enterprise: 'R 1,500 / month' },
  NG: {
    free: '₦ 0',
    smart: '₦ 5,000 / month',
    pro: '₦ 15,000 / month',
    enterprise: '₦ 50,000 / month',
  },
  ZW: {
    free: 'US$ 0',
    smart: 'US$ 10 / month',
    pro: 'US$ 30 / month',
    enterprise: 'US$ 100 / month',
  },
};

export default function Settings() {
  const { business, profile, setBusiness, signOut } = useAuthStore();
  const { lang, setLang, mode, setMode } = useUiStore();
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const setRegion = useRegionStore((s) => s.setRegion);
  const taxAuth = regionConfig.taxAuthority;
  const TIER_PRICE = TIER_PRICES[regionCode] ?? TIER_PRICES.BW;
  const toast = useToast();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // signOut already swallows errors internally; this is defensive only.
    }
    window.location.replace('/');
  };
  const [name, setName] = useState(business?.name ?? '');
  const [vatNumber, setVatNumber] = useState(business?.vat_number ?? '');
  const [vatRegistered, setVatRegistered] = useState(business?.vat_registered ?? false);
  const [saving, setSaving] = useState(false);

  // Brand uploads (clinic logo + practitioner avatar)
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  if (!business) return null;

  const save = async () => {
    setSaving(true);
    const { data, error } = await supabase
      .from('businesses')
      .update({ name, vat_number: vatNumber || null, vat_registered: vatRegistered })
      .eq('id', business.id)
      .select()
      .single();
    setSaving(false);
    if (error) {
      toast.show(error.message, { tone: 'error' });
      return;
    }
    setBusiness(data);
    toast.show('Saved.', { tone: 'success' });
  };

  const uploadBrand = async (file: File, kind: 'logo' | 'avatar'): Promise<string | null> => {
    // Path: {business_id}/{kind}/{timestamp}-{filename}
    // The leading business_id segment is what the RLS policy gates on.
    const safeName = file.name.replace(/[^a-z0-9._-]/gi, '_').toLowerCase();
    const path = `${business.id}/${kind}/${Date.now()}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from('brand-assets')
      .upload(path, file, { upsert: true, cacheControl: '3600' });
    if (upErr) {
      toast.show(`Upload failed: ${upErr.message}`, { tone: 'error' });
      return null;
    }
    const { data: pub } = supabase.storage.from('brand-assets').getPublicUrl(path);
    return pub.publicUrl;
  };

  const onLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.show('Please upload an image file.', { tone: 'error' });
      return;
    }
    setUploadingLogo(true);
    const url = await uploadBrand(file, 'logo');
    if (url) {
      const { data, error } = await supabase
        .from('businesses')
        .update({ logo_url: url })
        .eq('id', business.id)
        .select()
        .single();
      if (error) {
        toast.show(error.message, { tone: 'error' });
      } else {
        setBusiness(data);
        toast.show('Logo updated.', { tone: 'success' });
      }
    }
    setUploadingLogo(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const onAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    if (!file.type.startsWith('image/')) {
      toast.show('Please upload an image file.', { tone: 'error' });
      return;
    }
    setUploadingAvatar(true);
    const url = await uploadBrand(file, 'avatar');
    if (url) {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', profile.id);
      if (error) {
        toast.show(error.message, { tone: 'error' });
      } else {
        // Profile lives in the auth store; trigger a reload so the
        // sidebar avatar picks up the new URL without a hard refresh.
        await useAuthStore.getState().loadProfileAndBusiness(profile.id);
        toast.show('Avatar updated.', { tone: 'success' });
      }
    }
    setUploadingAvatar(false);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const removeLogo = async () => {
    const { data, error } = await supabase
      .from('businesses')
      .update({ logo_url: null })
      .eq('id', business.id)
      .select()
      .single();
    if (!error) {
      setBusiness(data);
      toast.show('Logo removed.', { tone: 'success' });
    }
  };

  const removeAvatar = async () => {
    if (!profile) return;
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: null })
      .eq('id', profile.id);
    if (!error) {
      await useAuthStore.getState().loadProfileAndBusiness(profile.id);
      toast.show('Avatar removed.', { tone: 'success' });
    }
  };

  return (
    <main className="mx-auto max-w-3xl space-y-3 px-4 py-4 md:px-6 md:py-6">
      <h1 className="font-display mb-2 text-2xl">Settings</h1>

      {/* Business */}
      <Card>
        <h2 className="font-display mb-3 text-lg">Business</h2>
        <div className="space-y-3">
          <Input label="Business name" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={vatRegistered}
              onChange={(e) => setVatRegistered(e.target.checked)}
            />
            VAT-registered with {taxAuth}
          </label>
          {vatRegistered && (
            <Input
              label="VAT number"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
            />
          )}
          <Button onClick={save} loading={saving}>
            Save
          </Button>
        </div>
      </Card>

      {/* Brand assets — clinic logo + practitioner avatar */}
      <Card>
        <h2 className="font-display mb-1 flex items-center gap-2 text-lg">
          <ImageIcon size={18} /> Brand
        </h2>
        <p className="text-muted mb-4 text-xs">
          Your logo appears on the sidebar, invoices, and patient receipts. Your avatar shows up on
          the dashboard greeting. PNG or JPG, ideally square, 256–512 px.
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          {/* Logo */}
          <div className="text-center">
            <div className="text-muted mb-2 text-xs font-medium">Clinic / business logo</div>
            <div className="border-line bg-paper-2 mx-auto flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl border">
              {business.logo_url ? (
                <img
                  src={business.logo_url}
                  alt={`${business.name} logo`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ImageIcon size={28} className="text-muted/60" />
              )}
            </div>
            <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={onLogoChange} />
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Upload size={14} />}
                loading={uploadingLogo}
                onClick={() => logoInputRef.current?.click()}
              >
                {business.logo_url ? 'Replace' : 'Upload logo'}
              </Button>
              {business.logo_url && (
                <Button size="sm" variant="ghost" onClick={removeLogo}>
                  Remove
                </Button>
              )}
            </div>
          </div>

          {/* Avatar */}
          <div className="text-center">
            <div className="text-muted mb-2 text-xs font-medium">Your profile picture</div>
            <div className="border-line bg-paper-2 mx-auto flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserCircle2 size={48} className="text-muted/60" />
              )}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onAvatarChange}
            />
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Upload size={14} />}
                loading={uploadingAvatar}
                onClick={() => avatarInputRef.current?.click()}
              >
                {profile?.avatar_url ? 'Replace' : 'Upload avatar'}
              </Button>
              {profile?.avatar_url && (
                <Button size="sm" variant="ghost" onClick={removeAvatar}>
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Subscription */}
      <Card>
        <h2 className="font-display mb-3 text-lg">Subscription</h2>
        <div className="flex items-center justify-between">
          <div>
            <Pill tone={business.tier === 'free' ? 'neutral' : 'accent'}>
              {business.tier.toUpperCase()}
            </Pill>
            <p className="text-muted mt-2 text-sm">{TIER_PRICE?.[business.tier] ?? ''}</p>
          </div>
          {business.tier === 'free' && (
            <Button variant="primary" disabled>
              Upgrade (coming)
            </Button>
          )}
        </div>
      </Card>

      {/* Region / Country */}
      <Card>
        <h2 className="font-display mb-3 flex items-center gap-2 text-lg">
          <Globe size={18} /> Region
        </h2>
        <p className="text-muted mb-3 text-sm">
          Controls currency, VAT rate, PAYE brackets, and tax authority labels.
        </p>
        <div className="flex flex-wrap gap-2">
          {(['BW', 'ZA', 'NG', 'ZW'] as RegionCode[]).map((code) => {
            const names: Record<string, string> = {
              BW: '🇧🇼 Botswana',
              ZA: '🇿🇦 South Africa',
              NG: '🇳🇬 Nigeria',
              ZW: '🇿🇼 Zimbabwe',
            };
            return (
              <Button
                key={code}
                variant={regionCode === code ? 'primary' : 'secondary'}
                onClick={() => setRegion(code)}
              >
                {names[code]}
              </Button>
            );
          })}
        </div>
      </Card>

      {/* Language */}
      <Card>
        <h2 className="font-display mb-3 flex items-center gap-2 text-lg">
          <Globe size={18} /> Language
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button variant={lang === 'en' ? 'primary' : 'secondary'} onClick={() => setLang('en')}>
            English
          </Button>
          <Button variant={lang === 'tn' ? 'primary' : 'secondary'} onClick={() => setLang('tn')}>
            Setswana
          </Button>
          <Button variant={lang === 'zu' ? 'primary' : 'secondary'} onClick={() => setLang('zu')}>
            isiZulu
          </Button>
          <Button variant={lang === 'ha' ? 'primary' : 'secondary'} onClick={() => setLang('ha')}>
            Hausa
          </Button>
          <Button variant={lang === 'pcm' ? 'primary' : 'secondary'} onClick={() => setLang('pcm')}>
            Pidgin
          </Button>
          <Button variant={lang === 'sn' ? 'primary' : 'secondary'} onClick={() => setLang('sn')}>
            Shona
          </Button>
        </div>
      </Card>

      {/* Display mode — operatory toggle (clinical dark theme) */}
      <Card>
        <h2 className="font-display mb-1 flex items-center gap-2 text-lg">
          <Moon size={18} /> Display
        </h2>
        <p className="text-muted mb-3 text-xs">
          Operatory Mode dims the UI for use in bright treatment rooms. Cached patient data + tooth
          chart assets stay available offline.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={mode === 'normal' ? 'primary' : 'secondary'}
            onClick={() => setMode('normal')}
          >
            Normal
          </Button>
          <Button
            variant={mode === 'operatory' ? 'primary' : 'secondary'}
            onClick={() => setMode('operatory')}
          >
            Operatory (dim)
          </Button>
          {mode === 'fun' && (
            <Pill tone="accent" className="self-center">
              🎉 Fun Mode
            </Pill>
          )}
        </div>
      </Card>

      {/* Profile */}
      {profile && (
        <Card>
          <h2 className="font-display mb-3 text-lg">Account</h2>
          <p className="text-muted text-sm">Signed in as</p>
          <p className="font-mono">{profile.email}</p>
        </Card>
      )}

      {/* Change password */}
      <ChangePasswordCard />

      {/* Two-factor authentication */}
      <TwoFactorCard requireForHealthcare={isHealthcareBusiness(business.industry)} />

      <Button variant="danger" full onClick={handleSignOut} leftIcon={<LogOut size={16} />}>
        Sign out
      </Button>

      <p className="text-muted pt-4 text-center text-xs">
        Nako Pula v0.2 · powered by Nako Tech Pty Ltd
      </p>
    </main>
  );
}

interface TotpFactor {
  id: string;
  friendly_name?: string;
  factor_type: string;
  status: 'verified' | 'unverified';
  created_at: string;
  updated_at: string;
}

function TwoFactorCard({ requireForHealthcare }: { requireForHealthcare: boolean }) {
  const toast = useToast();
  const [factors, setFactors] = useState<TotpFactor[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Enrollment state
  const [enrolling, setEnrolling] = useState(false);
  const [enrollFactorId, setEnrollFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      toast.show(error.message, { tone: 'error' });
      setFactors([]);
    } else {
      setFactors((data?.totp ?? []) as TotpFactor[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  const verified = (factors ?? []).filter((f) => f.status === 'verified');
  const isEnabled = verified.length > 0;

  const startEnroll = async () => {
    setEnrolling(true);
    setCode('');
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: `Authenticator (${new Date().toISOString().slice(0, 10)})`,
    });
    if (error || !data) {
      toast.show(error?.message ?? 'Could not start 2FA setup.', { tone: 'error' });
      setEnrolling(false);
      return;
    }
    setEnrollFactorId(data.id);
    setQr(data.totp.qr_code);
    setSecret(data.totp.secret);
  };

  const cancelEnroll = async () => {
    if (enrollFactorId) {
      await supabase.auth.mfa.unenroll({ factorId: enrollFactorId });
    }
    setEnrolling(false);
    setEnrollFactorId(null);
    setQr(null);
    setSecret(null);
    setCode('');
  };

  const verifyEnroll = async () => {
    if (!enrollFactorId || code.length < 6) {
      toast.show('Enter the 6-digit code from your authenticator app.', { tone: 'warning' });
      return;
    }
    setVerifying(true);
    const challenge = await supabase.auth.mfa.challenge({ factorId: enrollFactorId });
    if (challenge.error || !challenge.data) {
      setVerifying(false);
      toast.show(challenge.error?.message ?? 'Challenge failed.', { tone: 'error' });
      return;
    }
    const verify = await supabase.auth.mfa.verify({
      factorId: enrollFactorId,
      challengeId: challenge.data.id,
      code,
    });
    setVerifying(false);
    if (verify.error) {
      toast.show(verify.error.message, { tone: 'error' });
      return;
    }
    toast.show('Two-factor authentication enabled.', { tone: 'success' });
    setEnrolling(false);
    setEnrollFactorId(null);
    setQr(null);
    setSecret(null);
    setCode('');
    void refresh();
  };

  const removeFactor = async (factorId: string) => {
    if (
      !window.confirm(
        'Disable two-factor authentication? Your account will only need a password to sign in.',
      )
    ) {
      return;
    }
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      toast.show(error.message, { tone: 'error' });
      return;
    }
    toast.show('Two-factor authentication disabled.', { tone: 'success' });
    void refresh();
  };

  return (
    <Card>
      <h2 className="font-display mb-1 flex items-center gap-2 text-lg">
        {isEnabled ? (
          <ShieldCheck size={18} className="text-green" />
        ) : (
          <ShieldAlert size={18} className="text-amber-600" />
        )}
        Two-factor authentication (2FA)
      </h2>
      <p className="text-muted mb-3 text-sm">
        Adds a 6-digit code from your phone every time you sign in. Works with{' '}
        <strong>Microsoft Authenticator</strong>, Google Authenticator, Authy, or any standard TOTP
        app.
      </p>

      {requireForHealthcare && !isEnabled && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <strong>Strongly recommended.</strong> You handle patient health records. Enabling 2FA
          protects those records if your password is ever leaked.
        </div>
      )}

      {loading ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : isEnabled ? (
        <div className="space-y-2">
          {verified.map((f) => (
            <div
              key={f.id}
              className="border-line flex items-center justify-between rounded-md border p-3 text-sm"
            >
              <div className="flex items-center gap-2">
                <Smartphone size={16} className="text-muted" />
                <div>
                  <p className="font-medium">{f.friendly_name ?? 'Authenticator app'}</p>
                  <p className="text-muted text-xs">
                    Enrolled {new Date(f.created_at).toISOString().slice(0, 10)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => void removeFactor(f.id)}
                className="text-red inline-flex items-center gap-1 text-xs hover:underline"
              >
                <Trash2 size={12} /> Remove
              </button>
            </div>
          ))}
          <p className="text-muted mt-2 text-xs">
            To add another device, remove the current one first, then enrol again from the new
            device.
          </p>
        </div>
      ) : enrolling && qr && secret ? (
        <div className="space-y-3">
          <ol className="text-muted ml-5 list-decimal space-y-1 text-sm">
            <li>Open Microsoft Authenticator (or Google Authenticator) on your phone.</li>
            <li>
              Tap <strong>+ Add account</strong> → <strong>Other (TOTP)</strong> →{' '}
              <strong>Scan QR code</strong>.
            </li>
            <li>Point your phone at the square below.</li>
            <li>Type the 6-digit code your phone shows, then tap Verify.</li>
          </ol>
          <div className="flex flex-col items-center gap-2">
            {/* Supabase returns qr_code as an SVG data URL */}
            <img src={qr} alt="2FA QR code" className="h-44 w-44 rounded-md border" />
            <details className="text-muted text-xs">
              <summary className="cursor-pointer">Can't scan? Type this secret in instead</summary>
              <p className="bg-paper-2 mt-1 break-all rounded-md p-2 font-mono">{secret}</p>
            </details>
          </div>
          <Input
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            autoComplete="one-time-code"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={() => void cancelEnroll()}>
              Cancel
            </Button>
            <Button
              onClick={() => void verifyEnroll()}
              loading={verifying}
              disabled={code.length < 6}
            >
              Verify and enable
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => void startEnroll()} leftIcon={<ShieldCheck size={16} />}>
          Enable 2FA
        </Button>
      )}
    </Card>
  );
}

function ChangePasswordCard() {
  const toast = useToast();
  const profile = useAuthStore((s) => s.profile);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = async () => {
    if (pwd.length < 10) {
      toast.show('Password must be at least 10 characters.', { tone: 'warning' });
      return;
    }
    // Require at least 3 of: lowercase, uppercase, digit, symbol — modern
    // NIST guidance prefers length over forced character classes, but for
    // an SME bookkeeping product where the password defends financial data
    // a small complexity floor is worth the slight friction.
    const classes = [
      /[a-z]/.test(pwd),
      /[A-Z]/.test(pwd),
      /\d/.test(pwd),
      /[^a-zA-Z0-9]/.test(pwd),
    ].filter(Boolean).length;
    if (classes < 3) {
      toast.show('Use at least 3 of: lowercase, uppercase, number, symbol.', {
        tone: 'warning',
        durationMs: 4000,
      });
      return;
    }
    if (/^(password|nako|admin|qwerty|12345)/i.test(pwd)) {
      toast.show('That password is too common — please pick something less guessable.', {
        tone: 'warning',
      });
      return;
    }
    if (pwd !== pwd2) {
      toast.show('Passwords do not match.', { tone: 'warning' });
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    if (error) {
      setBusy(false);
      toast.show(error.message, { tone: 'error' });
      return;
    }
    // If a temp-password flag was set on this profile (admin-issued),
    // clear it so the force-reset modal does not reappear next session.
    if (profile?.must_change_password && profile?.id) {
      await supabase.from('profiles').update({ must_change_password: false }).eq('id', profile.id);
      useAuthStore.setState((s) => ({
        ...s,
        profile: s.profile ? { ...s.profile, must_change_password: false } : s.profile,
      }));
    }
    setBusy(false);
    setPwd('');
    setPwd2('');
    toast.show('Password changed. Use it next time you sign in.', { tone: 'success' });
  };

  return (
    <Card>
      <h2 className="font-display mb-3 flex items-center gap-2 text-lg">
        <KeyRound size={18} /> Password
      </h2>
      <p className="text-muted mb-3 text-sm">
        Change the password you use to sign in. After saving, write the new value into your
        Credentials file so you don&apos;t lose it.
      </p>
      <div className="space-y-3">
        <Input
          type={show ? 'text' : 'password'}
          label="New password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          autoComplete="new-password"
        />
        <Input
          type={show ? 'text' : 'password'}
          label="Confirm new password"
          value={pwd2}
          onChange={(e) => setPwd2(e.target.value)}
          autoComplete="new-password"
        />
        <label className="text-muted flex items-center gap-2 text-xs">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          Show passwords
        </label>
        <Button onClick={change} loading={busy} disabled={!pwd || !pwd2}>
          Change password
        </Button>
      </div>
    </Card>
  );
}
